'use client';

import { useState } from 'react';
import {
  CreditCard,
  Download,
  ArrowUpRight,
  Check,
  Receipt,
  Sparkles,
  Loader2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Modal,
} from '@/components/ui';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import {
  UsageMeter,
  PlanSelector,
} from '@/components/subscription';
import {
  createCheckoutSession,
  createCustomerPortalSession,
  redirectToCheckout,
  getPriceId,
  isStripeConfigured,
  TRIAL_DAYS,
} from '@/services/stripe';
import { useAuthStore } from '@/stores/authStore';
import type { SubscriptionPlan, BillingInterval } from '@/types/subscription';

const BillingPage = () => {
  const { tier, status, plan, billing, isTrialing, trialDaysRemaining, usage } = useSubscription();
  const { paymentMethods, invoices } = useSubscriptionStore();
  const { user, organization } = useAuthStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const handleSelectPlan = async (selectedPlan: SubscriptionPlan, interval: BillingInterval) => {
    if (!user || !organization) {
      setCheckoutError('Please log in to continue');
      return;
    }

    if (selectedPlan.tier === 'free') {
      setShowUpgradeModal(false);
      return;
    }

    if (!isStripeConfigured()) {
      setCheckoutError('Payment system is not configured');
      return;
    }

    setIsCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const priceId = getPriceId(
        selectedPlan.tier as Exclude<typeof selectedPlan.tier, 'free'>,
        interval
      );

      const { url } = await createCheckoutSession({
        priceId,
        organizationId: organization.id,
        userId: user.id,
        trialDays: TRIAL_DAYS,
      });

      redirectToCheckout(url);
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!organization?.stripe_customer_id) {
      setCheckoutError('No subscription found');
      return;
    }

    setIsPortalLoading(true);
    try {
      const { url } = await createCustomerPortalSession({
        customerId: organization.stripe_customer_id,
      });
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
      setCheckoutError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setIsPortalLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-secondary-100">
          Billing & Subscription
        </h1>
        <p className="mt-1 text-gray-500 dark:text-secondary-400">
          Manage your subscription, payment methods, and billing history
        </p>
      </div>

      {/* Trial Banner */}
      {isTrialing && (
        <Card className="border-primary-200 dark:border-primary-500/30 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-500/10 dark:to-primary-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-primary-500 p-2">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-primary-900 dark:text-primary-100">
                    Professional Trial
                  </p>
                  <p className="text-sm text-primary-700 dark:text-primary-300">
                    {trialDaysRemaining} days remaining in your free trial
                  </p>
                </div>
              </div>
              <Button variant="primary" onClick={() => setShowUpgradeModal(true)}>
                Upgrade Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Current Plan</CardTitle>
            <Badge
              color={status === 'active' ? 'green' : status === 'trialing' ? 'blue' : 'yellow'}
            >
              {status === 'active' ? 'Active' : status === 'trialing' ? 'Trial' : status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-secondary-100">
                  {plan.name}
                </h3>
                {plan.popular && (
                  <Badge color="blue" size="sm">
                    Popular
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-gray-500 dark:text-secondary-400">{plan.description}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900 dark:text-secondary-100">
                {formatCurrency(billing?.amount || plan.pricing.monthly)}
                <span className="text-base font-normal text-gray-500 dark:text-secondary-400">
                  /mo
                </span>
              </p>
              {billing && (
                <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
                  Renews {formatDate(billing.nextBillingDate)}
                </p>
              )}
            </div>
          </div>

          {/* Plan Features */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-secondary-300">
                Included
              </p>
              <ul className="mt-2 space-y-1.5">
                {plan.limits.maxUnits && (
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-secondary-400">
                    <Check className="h-4 w-4 text-success-500" />
                    Up to {plan.limits.maxUnits} units
                  </li>
                )}
                {plan.limits.maxUsers && (
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-secondary-400">
                    <Check className="h-4 w-4 text-success-500" />
                    {plan.limits.maxUsers} team members
                  </li>
                )}
                {plan.features.iotMonitoring && (
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-secondary-400">
                    <Check className="h-4 w-4 text-success-500" />
                    IoT device monitoring
                  </li>
                )}
                {plan.features.advancedAnalytics && (
                  <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-secondary-400">
                    <Check className="h-4 w-4 text-success-500" />
                    Advanced analytics
                  </li>
                )}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowUpgradeModal(true)}>
                <ArrowUpRight className="h-4 w-4 mr-2" />
                {tier === 'enterprise' ? 'Contact Sales' : 'Upgrade Plan'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Usage This Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <UsageMeter limit="maxUnits" currentValue={usage.currentUnits} />
            <UsageMeter limit="maxUsers" currentValue={usage.currentUsers} />
            <UsageMeter limit="maxDevices" currentValue={usage.currentDevices} />
            <UsageMeter limit="maxProperties" currentValue={usage.currentProperties} />
            <UsageMeter
              limit="maxWorkOrdersPerMonth"
              currentValue={usage.workOrdersThisMonth}
            />
            <UsageMeter limit="storageGB" currentValue={usage.storageUsedGB} />
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
              Payment Methods
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowPaymentModal(true)}>
              Add Method
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {paymentMethods.length > 0 ? (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 dark:border-white/[0.06] p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded bg-gray-100 dark:bg-secondary-700 p-2">
                      <CreditCard className="h-5 w-5 text-gray-600 dark:text-secondary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-secondary-100">
                        {method.brand} ending in {method.last4}
                      </p>
                      {method.expiryMonth && method.expiryYear && (
                        <p className="text-sm text-gray-500 dark:text-secondary-400">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.isDefault && (
                      <Badge color="green" size="sm">
                        Default
                      </Badge>
                    )}
                    <Button variant="ghost" size="sm">
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="mx-auto h-12 w-12 text-gray-300 dark:text-secondary-600" />
              <p className="mt-4 text-gray-500 dark:text-secondary-400">
                No payment methods on file
              </p>
              <Button
                variant="primary"
                className="mt-4"
                onClick={() => setShowPaymentModal(true)}
              >
                Add Payment Method
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-white/[0.06]">
                    <th className="pb-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">
                      Invoice
                    </th>
                    <th className="pb-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">
                      Date
                    </th>
                    <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">
                      Amount
                    </th>
                    <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">
                      Status
                    </th>
                    <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">
                      Download
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="border-b border-gray-50 dark:border-white/[0.03]"
                    >
                      <td className="py-3 font-medium text-gray-900 dark:text-secondary-100">
                        {invoice.number}
                      </td>
                      <td className="py-3 text-gray-600 dark:text-secondary-300">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="py-3 text-right text-gray-900 dark:text-secondary-100">
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td className="py-3 text-right">
                        <Badge
                          color={
                            invoice.status === 'paid'
                              ? 'green'
                              : invoice.status === 'open'
                              ? 'blue'
                              : 'gray'
                          }
                          size="sm"
                        >
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Receipt className="mx-auto h-12 w-12 text-gray-300 dark:text-secondary-600" />
              <p className="mt-4 text-gray-500 dark:text-secondary-400">No invoices yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Modal */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => !isCheckoutLoading && setShowUpgradeModal(false)}
        title="Choose Your Plan"
        size="xl"
      >
        {isCheckoutLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            <p className="mt-4 text-gray-600 dark:text-secondary-400">
              Redirecting to checkout...
            </p>
          </div>
        ) : (
          <>
            {checkoutError && (
              <div className="mb-4 rounded-lg bg-danger-50 dark:bg-danger-500/10 border border-danger-200 dark:border-danger-500/30 p-4">
                <p className="text-sm text-danger-700 dark:text-danger-400">{checkoutError}</p>
              </div>
            )}
            <PlanSelector
              onSelectPlan={handleSelectPlan}
              showComparison
              highlightCurrent
            />
          </>
        )}
      </Modal>

      {/* Payment Method Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => !isPortalLoading && setShowPaymentModal(false)}
        title="Manage Payment Methods"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-secondary-700 p-6 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" />
            <p className="mt-4 text-gray-600 dark:text-secondary-400">
              Manage your payment methods through Stripe's secure portal
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-secondary-500">
              Add, update, or remove payment methods safely
            </p>
          </div>
          {checkoutError && (
            <div className="rounded-lg bg-danger-50 dark:bg-danger-500/10 border border-danger-200 dark:border-danger-500/30 p-3">
              <p className="text-sm text-danger-700 dark:text-danger-400">{checkoutError}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleManageSubscription}
              disabled={isPortalLoading || !organization?.stripe_customer_id}
            >
              {isPortalLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opening...
                </>
              ) : (
                'Open Stripe Portal'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export { BillingPage };
